export type DomainStatus = "available" | "mirror" | "maintenance";

export interface DomainEntry {
  id: string;
  label: string;
  host: string;
  status: DomainStatus;
  region: string;
  latency: string;
  note: string;
}

export interface MirrorEntry {
  id: string;
  title: string;
  summary: string;
  host: string;
  tags: string[];
  region: string;
  status: DomainStatus;
  latency: string;
  updated: string;
}

export interface UpdateEntry {
  id: string;
  date: string;
  tag: string;
  title: string;
  summary: string;
}

export interface GuideStep {
  id: string;
  index: string;
  title: string;
  desc: string;
}

export interface CollectionEntry {
  id: string;
  title: string;
  summary: string;
  count: string;
  image: string;
}

export interface FaqEntry {
  id: string;
  question: string;
  answer: string;
}

export const siteMeta = {
  name: "琉璃神社",
  slogan: "最新地址 · 可用域名 · 镜像入口",
  intro:
    "原网址打不开时，不用再到处翻帖。这里把当前可访问的域名、镜像节点与防走失方法集中整理成一份入口索引，收藏一次，随时能回到这里。",
  updatedAt: "2026-10-10",
  nextCheck: "每日 09:00 / 21:00 自动巡检",
};

export const heroStats = [
  { id: "s1", value: "6", label: "当前在线入口" },
  { id: "s2", value: "3", label: "可用镜像节点" },
  { id: "s3", value: "24h", label: "入口巡检频率" },
  { id: "s4", value: "99.2%", label: "近 30 天可用率" },
];

export const domains: DomainEntry[] = [
  {
    id: "d1",
    label: "主站直连",
    host: "liuli-shrine.com",
    status: "available",
    region: "全球",
    latency: "184 ms",
    note: "证书正常，推荐首选",
  },
  {
    id: "d2",
    label: "主站备用",
    host: "liuli-shrine.net",
    status: "available",
    region: "全球",
    latency: "212 ms",
    note: "主站拥堵时切换",
  },
  {
    id: "d3",
    label: "经典域名",
    host: "ll-miaoshe.org",
    status: "available",
    region: "东亚",
    latency: "96 ms",
    note: "老入口，稳定性高",
  },
  {
    id: "d4",
    label: "加速镜像",
    host: "liuli.cc-acc.top",
    status: "mirror",
    region: "香港",
    latency: "58 ms",
    note: "CDN 加速，移动端友好",
  },
  {
    id: "d5",
    label: "备用镜像",
    host: "liuli-node.sg",
    status: "mirror",
    region: "新加坡",
    latency: "143 ms",
    note: "海外线路备选",
  },
  {
    id: "d6",
    label: "临时维护",
    host: "liuli-vip.xyz",
    status: "maintenance",
    region: "北美",
    latency: "—",
    note: "线路调整中，预计 24h 内恢复",
  },
];

export const mirrors: MirrorEntry[] = [
  {
    id: "m1",
    title: "主站 HTTPS 直连",
    summary:
      "官方主域，全程加密，页面与更新最快同步。适合长期使用，建议设为第一收藏入口。",
    host: "liuli-shrine.com",
    tags: ["官方", "HTTPS", "推荐"],
    region: "全球",
    status: "available",
    latency: "184 ms",
    updated: "10-10 08:40",
  },
  {
    id: "m2",
    title: "香港 CDN 加速入口",
    summary:
      "面向移动网络做了首屏优化，弱网环境下加载更稳，适合手机随时打开查看入口。",
    host: "liuli.cc-acc.top",
    tags: ["加速", "移动端", "低延迟"],
    region: "香港",
    status: "available",
    latency: "58 ms",
    updated: "10-10 08:12",
  },
  {
    id: "m3",
    title: "经典域名 ll-miaoshe",
    summary:
      "沿用多年的老入口，被不少用户长期收藏。线路成熟，适合作为主站的第一备用。",
    host: "ll-miaoshe.org",
    tags: ["经典", "稳定"],
    region: "东亚",
    status: "available",
    latency: "96 ms",
    updated: "10-09 22:30",
  },
  {
    id: "m4",
    title: "新加坡节点镜像",
    summary:
      "海外线路镜像，主站与香港线路都超时时可尝试此入口，内容与主站保持一致。",
    host: "liuli-node.sg",
    tags: ["海外", "备选"],
    region: "新加坡",
    status: "available",
    latency: "143 ms",
    updated: "10-09 19:05",
  },
  {
    id: "m5",
    title: "备用主域 .net",
    summary:
      "与主站内容相同，作为主域拥挤时的分流入口，出现「主站打不开」时可优先切换。",
    host: "liuli-shrine.net",
    tags: ["分流", "备份"],
    region: "全球",
    status: "available",
    latency: "212 ms",
    updated: "10-09 15:48",
  },
  {
    id: "m6",
    title: "北美线路（维护中）",
    summary:
      "正在更换线路设备，暂时不可用。恢复后会在更新日志里第一时间标注，请先用其他入口。",
    host: "liuli-vip.xyz",
    tags: ["维护中", "请稍候"],
    region: "北美",
    status: "maintenance",
    latency: "—",
    updated: "10-08 11:20",
  },
];

export const updates: UpdateEntry[] = [
  {
    id: "u1",
    date: "2026-10-10",
    tag: "巡检",
    title: "今日入口巡检完成，6 个入口在线",
    summary: "主站与香港节点响应正常，北美线路仍在维护，预计 24 小时内恢复。",
  },
  {
    id: "u2",
    date: "2026-10-09",
    tag: "新增",
    title: "新增新加坡节点镜像入口",
    summary: "针对海外访问新增一条镜像线路，已加入镜像索引，内容与主站保持一致。",
  },
  {
    id: "u3",
    date: "2026-10-08",
    tag: "证书",
    title: "主域完成 HTTPS 证书续期",
    summary: "liuli-shrine.com 证书已更新，浏览器不再提示不安全，可放心收藏使用。",
  },
  {
    id: "u4",
    date: "2026-10-06",
    tag: "调整",
    title: "备用主域切换至分流模式",
    summary: "当主站访问量偏高时，会自动引导部分用户使用 .net 备用域，减少拥堵。",
  },
  {
    id: "u5",
    date: "2026-10-03",
    tag: "提示",
    title: "部分网络运营商 DNS 污染提醒",
    summary: "少数运营商可能污染解析结果，遇到打不开可更换 DNS 或直接使用镜像入口。",
  },
];

export const guideSteps: GuideStep[] = [
  {
    id: "g1",
    index: "01",
    title: "一次性收藏多个入口",
    desc: "不要只存一个网址。把主站、经典域名和至少一个镜像都加入浏览器书签，单点失效时能立刻切换。",
  },
  {
    id: "g2",
    index: "02",
    title: "记住关键词，而不是记死网址",
    desc: "域名会变，关键词不会。记住站点名称即可，通过搜索随时找回这篇入口整理页。",
  },
  {
    id: "g3",
    index: "03",
    title: "回来看更新日志",
    desc: "入口状态每天巡检两次，有新增或维护都会记在更新日志里，先看日志再决定用哪个入口。",
  },
  {
    id: "g4",
    index: "04",
    title: "保存页面到本地",
    desc: "对整页截图或另存为书签文件夹，即使临时断网，也能对着本地记录逐个尝试入口。",
  },
];

export const collections: CollectionEntry[] = [
  {
    id: "c1",
    title: "全站入口总览",
    summary: "把所有可访问入口按状态、线路、延迟整理成一张总表，一眼看清哪个能用。",
    count: "6 个入口",
    image:
      "https://readdy.ai/api/search-image?query=celadon%20green%20glazed%20ceramic%20tiles%20arranged%20in%20a%20neat%20grid%20on%20warm%20cream%20background%2C%20soft%20studio%20lighting%2C%20minimal%20oriental%20still%20life%2C%20elegant%20editorial%20photography&width=800&height=600&seq=liuli-col-overview-01&orientation=landscape",
  },
  {
    id: "c2",
    title: "镜像节点速查",
    summary: "按地区列出镜像节点与实时延迟，帮你在主站卡顿时快速挑一条顺畅线路。",
    count: "3 个节点",
    image:
      "https://readdy.ai/api/search-image?query=abstract%20network%20of%20glowing%20glass%20nodes%20connected%20by%20fine%20lines%2C%20jade%20green%20and%20soft%20gold%20glow%2C%20warm%20dark%20background%2C%20clean%20minimal%20digital%20art&width=800&height=600&seq=liuli-col-mirror-02&orientation=landscape",
  },
  {
    id: "c3",
    title: "移动端入口精选",
    summary: "为手机场景挑选的低延迟、弱网友好入口，扫码或复制即可在移动端打开。",
    count: "2 个入口",
    image:
      "https://readdy.ai/api/search-image?query=minimal%20smartphone%20on%20warm%20cream%20desk%20beside%20a%20small%20celadon%20ceramic%20cup%2C%20soft%20daylight%2C%20calm%20oriental%20lifestyle%20photography&width=800&height=600&seq=liuli-col-mobile-03&orientation=landscape",
  },
];

export const faqs: FaqEntry[] = [
  {
    id: "f1",
    question: "为什么原来的官网突然进不去了？",
    answer:
      "常见原因有三类：域名更换、所在网络运营商的 DNS 解析异常，或线路临时维护。先看页面顶部的入口速览和更新日志，通常能立刻找到替代入口。",
  },
  {
    id: "f2",
    question: "镜像站和主站内容一样吗？",
    answer:
      "本页列出的镜像用于分流访问，内容与主站保持同步，只是线路和加载速度不同。主站可用时优先用主站，访问不畅时再切换到镜像。",
  },
  {
    id: "f3",
    question: "这些入口多久更新一次？",
    answer:
      "入口状态每天巡检两次，分别在上午 09:00 与晚上 21:00。出现新增、失效或恢复都会同步记录到更新日志。",
  },
  {
    id: "f4",
    question: "收藏的入口全部打不开了怎么办？",
    answer:
      "先尝试更换 DNS 或切换网络（例如从 Wi-Fi 切到移动数据）。如果仍不行，回到本页查看最新地址，镜像索引里通常会有可用线路。",
  },
  {
    id: "f5",
    question: "怎么才能不再走失？",
    answer:
      "按「防走失指引」做四件事：多收藏几个入口、记住关键词、定期回来看更新日志、把本页保存到本地。做到这几点基本不会再迷路。",
  },
];